/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import type { Tag } from '../../../../services/api/api.tags'

interface ModalEditProps {
  tag?: Tag
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ tag, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!tag)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)

  const doSave = async (values: any) => {
    try {
      let apiCall: Promise<any>

      if (isEdit) {
        apiCall = context.api.tags.updateTag(values.id, values)
      } else {
        apiCall = context.api.tags.createTag(values)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(translate(isEdit ? 'SETTINGS_TAG_EDITED_SUCCESS_MSG' : 'SETTINGS_TAG_ADDED_SUCCESS_MSG'))

      // Refresh the table
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      toast.error(errorMsg)
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'SETTINGS_TAG_EDIT_TITLE' : 'SETTINGS_TAG_ADD_TITLE')}
      size="md"
      dataTestId="edit-tag-modal"
    >
      <EditForm onSubmit={doSave} doClose={doClose} isEdit={isEdit} tag={tag} loading={loading} />
    </Modal>
  )
}

export default ModalEdit
